import { useState } from "react";
import { Bot } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useBots, useCreateBot, useDeleteBot } from "@/features/bots/hooks";
import { getApiErrorMessage } from "@/lib/api";
import type { Bot as BotType } from "@/types/api";

export function BotsPage() {
  const { t } = useTranslation(["bots", "common", "errors"]);
  const { data: bots, isLoading, isError } = useBots();
  const createBot = useCreateBot();
  const deleteBot = useDeleteBot();

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [language, setLanguage] = useState("es");
  const [timezone, setTimezone] = useState("America/Argentina/Buenos_Aires");
  const [error, setError] = useState<string | null>(null);

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    createBot.mutate(
      { name, language, timezone },
      {
        onSuccess: () => {
          setShowCreate(false);
          setName("");
          toast.success(t("bots:list.created"));
        },
        onError: (err) => setError(getApiErrorMessage(err)),
      },
    );
  }

  function handleDelete(bot: BotType) {
    if (window.confirm(t("bots:list.deleteConfirm", { name: bot.name }))) {
      deleteBot.mutate(bot.id, {
        onSuccess: () => toast.success(t("bots:list.deleted")),
      });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t("bots:list.title")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("bots:list.subtitle")}</p>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate((v) => !v)}>
          {t("bots:list.newBot")}
        </button>
      </div>

      {showCreate && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-base font-semibold">{t("bots:list.createTitle")}</h2>
          </div>
          <form className="card-body grid grid-cols-1 gap-4 md:grid-cols-3" onSubmit={handleCreate}>
            <div>
              <label className="text-sm font-medium text-slate-700">{t("bots:list.name")}</label>
              <input
                className="input mt-1 w-full"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">{t("bots:list.language")}</label>
              <select className="input mt-1 w-full" value={language} onChange={(e) => setLanguage(e.target.value)}>
                <option value="es">{t("bots:list.languageOptions.es")}</option>
                <option value="en">{t("bots:list.languageOptions.en")}</option>
                <option value="pt">{t("bots:list.languageOptions.pt")}</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">{t("bots:list.timezone")}</label>
              <input className="input mt-1 w-full" value={timezone} onChange={(e) => setTimezone(e.target.value)} />
            </div>
            {error && <p className="text-sm text-red-600 md:col-span-3">{error}</p>}
            <div className="md:col-span-3">
              <button type="submit" className="btn-primary" disabled={createBot.isPending}>
                {createBot.isPending ? t("common:states.creating") : t("bots:list.createBot")}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        {isLoading && <div className="card-body text-sm text-slate-500">{t("bots:list.loading")}</div>}
        {isError && <div className="card-body text-sm text-red-600">{t("bots:list.loadError")}</div>}
        {!isLoading && !isError && (!bots || bots.length === 0) && (
          <div className="card-body text-sm text-slate-500">{t("bots:list.empty")}</div>
        )}
        {!isLoading && !isError && bots && bots.length > 0 && (
          <ul className="divide-y divide-slate-200">
            {bots.map((bot) => (
              <li key={bot.id} className="flex items-center justify-between px-6 py-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                    <Bot className="h-5 w-5" />
                  </span>
                  <div>
                    <Link to={`/bots/${bot.id}`} className="font-medium text-slate-900 hover:text-brand-700">
                      {bot.name}
                    </Link>
                    <p className="text-xs text-slate-500">
                      {bot.language.toUpperCase()} · {bot.timezone} ·{" "}
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                          bot.status === "active"
                            ? "bg-green-100 text-green-800"
                            : bot.status === "suspended"
                              ? "bg-red-100 text-red-800"
                              : bot.status === "cancelled"
                                ? "bg-slate-100 text-slate-600"
                                : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {t(`bots:detail.status.${bot.status}`, { defaultValue: bot.status })}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Link to={`/bots/${bot.id}/onboarding`} className="text-sm text-brand-700 hover:underline">
                    {t("bots:list.whatsappLink")}
                  </Link>
                  <button className="text-sm text-red-600 hover:underline" onClick={() => handleDelete(bot)}>
                    {t("common:actions.delete")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
